

// StorageIndicator.jsx - Integrated with real API
import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { 
  Storage as StorageIcon,
  InsertDriveFile as FileIcon,
  Dns as DatabaseIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useAuth } from 'auth/Auth';

const StorageIndicator = () => {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { axiosInstance } = useAuth();

  // Storage limits (you can make these configurable later)
  const STORAGE_LIMITS = {
    file: 5, // GB - adjust as needed
    data: 0.2,  // GB - adjust as needed
  };

  useEffect(() => {
    fetchStorageData();
  }, []);

  const fetchStorageData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/storage-usage/', {
        params: {
          self_tenant: true
        }
      });

      const apiData = response.data;
      
      // Parse storage values (remove units and convert to numbers)
      const fileStorageGB = parseFloat(apiData.file_storage_gb.split(' ')[0]);
      const tableStorageGB = parseFloat(apiData.table_storage_gb.split(' ')[0]);
      const totalStorageGB = parseFloat(apiData.total_storage_gb.split(' ')[0]);

      // Create file type breakdown (simulated based on total file storage)
      // You can remove this if you want just total file storage
      const fileTypeBreakdown = {
        pdf: { used: fileStorageGB * 0.3, color: '#ef4444' }, // 30% PDFs
        images: { used: fileStorageGB * 0.4, color: '#3b82f6' }, // 40% Images  
        documents: { used: fileStorageGB * 0.2, color: '#10b981' }, // 20% Docs
        other: { used: fileStorageGB * 0.1, color: '#f59e0b' } // 10% Other
      };

      const processedData = {
        tenant: apiData.tenant,
        fileStorage: {
          totalLimit: STORAGE_LIMITS.file,
          totalUsed: fileStorageGB,
          types: fileTypeBreakdown
        },
        dataStorage: {
          totalLimit: STORAGE_LIMITS.data,
          used: tableStorageGB
        },
        rawData: apiData // Keep original data for reference
      };
      
      setStorageData(processedData);
    } catch (error) {
      console.error('Error fetching storage data:', error);
      setError(error.message || 'Failed to fetch storage data');
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (sizeInGB) => {
    if (sizeInGB >= 1) {
      return `${sizeInGB.toFixed(1)} GB`;
    } else {
      return `${(sizeInGB * 1024).toFixed(0)} MB`;
    }
  };

  const getUsagePercentage = (used, total) => {
    return Math.min((used / total) * 100, 100); // Cap at 100%
  };

  // Loading state
  if (loading) {
    return (
      <MDBox 
        sx={{ 
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          p: 2.5,
          border: '1px solid #e2e8f0',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 2
        }}
      >
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <LinearProgress 
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': { backgroundColor: '#64748b' }
            }}
          />
          <LinearProgress 
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': { backgroundColor: '#64748b' }
            }}
          />
        </Box>
      </MDBox>
    );
  }

  // Error state
  if (error) {
    return (
      <MDBox 
        sx={{ 
          backgroundColor: '#fef2f2',
          borderRadius: '12px',
          p: 2.5,
          border: '1px solid #fecaca',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 2
        }}
      >
        <MDTypography 
          variant="caption" 
          sx={{ 
            color: '#dc2626',
            fontSize: '0.75rem',
            textAlign: 'center'
          }}
        >
          Failed to load storage data
          <br />
          <span style={{ fontSize: '0.65rem' }}>{error}</span>
        </MDTypography>
      </MDBox>
    );
  }

  if (!storageData) return null;

  const { fileStorage, dataStorage } = storageData;
  const fileAvailable = fileStorage.totalLimit - fileStorage.totalUsed;
  const dataAvailable = dataStorage.totalLimit - dataStorage.used;

  // File type icons and labels
  const fileTypeIcons = {
    pdf: PdfIcon,
    images: ImageIcon,
    documents: DocIcon,
    other: CodeIcon
  };

  const fileTypeLabels = {
    pdf: 'PDFs',
    images: 'Images', 
    documents: 'Docs',
    other: 'Other'
  };

  return (
    <MDBox 
      sx={{ 
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        p: 2.5,
        border: '1px solid #e2e8f0',
        transition: 'all 0.2s ease',
        mx: 2,
        height: '100px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          backgroundColor: '#f1f5f9',
          borderColor: '#cbd5e1'
        }
      }}
    >
      {/* Main Content - Horizontal Layout */}
      <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 3, height: '100%' }}>
        
        {/* Left Section - Storage Bars */}
        <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: '220px' }}>
          
          {/* File Storage */}
          <MDBox>
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <FileIcon sx={{ fontSize: 14, color: '#64748b' }} />
              <MDTypography 
                variant="caption" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  minWidth: '35px'
                }}
              >
                Files
              </MDTypography>
              <Box
                sx={{
                  width: '120px',
                  height: 8,
                  borderRadius: '4px',
                  backgroundColor: '#e2e8f0',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                {(() => {
                  let currentPosition = 0;
                  return Object.entries(fileStorage.types).map(([type, data]) => {
                    const percentage = getUsagePercentage(data.used, fileStorage.totalLimit);
                    const result = (
                      <Tooltip key={type} title={`${fileTypeLabels[type]}: ${formatSize(data.used)}`} arrow>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${currentPosition}%`,
                            top: 0,
                            height: '100%',
                            width: `${percentage}%`,
                            backgroundColor: data.color,
                            transition: 'all 0.3s ease'
                          }}
                        />
                      </Tooltip>
                    );
                    currentPosition += percentage;
                    return result;
                  });
                })()}
                
                {/* Show warning if over limit */}
                {fileStorage.totalUsed > fileStorage.totalLimit && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      width: '4px',
                      backgroundColor: '#dc2626',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                )}
              </Box>
              <MDTypography 
                variant="caption" 
                sx={{ 
                  color: fileStorage.totalUsed > fileStorage.totalLimit ? '#dc2626' : '#475569',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  textAlign: 'right'
                }}
              >
                {formatSize(fileStorage.totalUsed)}/{formatSize(fileStorage.totalLimit)}
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Data Storage */}
          <MDBox>
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DatabaseIcon sx={{ fontSize: 14, color: '#64748b' }} />
              <MDTypography 
                variant="caption" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  minWidth: '35px'
                }}
              >
                Data
              </MDTypography>
              <Box
                sx={{
                  width: '120px',
                  height: 8,
                  borderRadius: '4px',
                  backgroundColor: '#e2e8f0',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Tooltip title={`Database: ${formatSize(dataStorage.used)}`} arrow>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${getUsagePercentage(dataStorage.used, dataStorage.totalLimit)}%`,
                      backgroundColor: '#8b5cf6',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </Tooltip>
                
                {/* Show warning if over limit */}
                {dataStorage.used > dataStorage.totalLimit && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      width: '4px',
                      backgroundColor: '#dc2626',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                )}
              </Box>
              <MDTypography 
                variant="caption" 
                sx={{ 
                  color: dataStorage.used > dataStorage.totalLimit ? '#dc2626' : '#475569',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  textAlign: 'right'
                }}
              >
                {formatSize(dataStorage.used)}/{formatSize(dataStorage.totalLimit)}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Right Section - Summary & Legend */}
        <MDBox sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
          

          {/* Legend */}
          <MDBox 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
              alignItems: 'center'
            }}
          >
            {/* File Types */}
            {Object.entries(fileStorage.types).map(([type, data]) => {
              const IconComponent = fileTypeIcons[type];
              return (
                                <MDBox key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: data.color,
                      flexShrink: 0
                    }}
                  />
                  <IconComponent sx={{ fontSize: 10, color: '#64748b', flexShrink: 0 }} />
                  <MDTypography 
                    variant="caption" 
                    sx={{ 
                      color: '#64748b',
                      fontSize: '0.65rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {fileTypeLabels[type]}
                  </MDTypography>
                </MDBox>
              );
            })}

            {/* Divider */}
            <Box
              sx={{
                width: '1px',
                height: '10px',
                backgroundColor: '#cbd5e1'
              }}
            />

            {/* Database */}
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#8b5cf6',
                  flexShrink: 0
                }}
              />
              <DatabaseIcon sx={{ fontSize: 10, color: '#64748b', flexShrink: 0 }} />
              <MDTypography 
                variant="caption" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}
              >
                Records
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Refresh data periodically */}
      {/* {process.env.NODE_ENV === 'development' && (
        <MDBox
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            cursor: 'pointer',
            opacity: 0.5,
            '&:hover': { opacity: 1 }
          }}
          onClick={fetchStorageData}
        >
          <MDTypography variant="caption" sx={{ fontSize: '0.6rem' }}>
            🔄
          </MDTypography>
        </MDBox>
      )} */}
    </MDBox>
  );
};

export default StorageIndicator;